import classNames from "classnames";
import {
    KeyboardEvent,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import styles from "../ChartOfAccountsTemplateEditor.module.scss";

interface Props {
    value: string;
    onCommit: (value: string) => void;
    placeholder?: string;
    addText?: string;
    readonly?: boolean;
    hasError?: boolean;
    inputMode?: "text" | "numeric";
    // Optional decoration rendered alongside the value in display mode (e.g. a
    // "Var" tag); ignored while editing.
    displayAddon?: ReactNode;
    // Optional class applied to BOTH the display button and the edit input so a
    // caller can match the row's typography (e.g. custom group rows render at the
    // structural-heading weight). The display button forces weight 400 from a
    // cascade layer, so an unlayered class here wins.
    valueClassName?: string;
    // When true, an empty value always shows the "Add …" affordance in the
    // warning (issue) color instead of the hover-only gray, so a missing required
    // value (e.g. a blank account name) is spottable at a glance.
    flagWhenEmpty?: boolean;
    // Editing ended with nothing in the field (Escape, or a blur that committed
    // nothing). A row that only exists to be named uses this to remove itself.
    onBlankExit?: () => void;
}

// Lightweight inline editor: renders the value as an EditableCellContent button
// that swaps to a text input on click. Commits on blur/Enter, cancels on
// Escape. Used by the Code and Account name cells. Issue-bar navigation enters
// edit mode by clicking the display button via the DOM (see content), so this
// field just needs to focus the input whenever it becomes editable.
export const EditorInlineEditField = ({
    value,
    onCommit,
    placeholder,
    addText,
    readonly,
    hasError,
    inputMode = "text",
    displayAddon,
    valueClassName,
    flagWhenEmpty,
    onBlankExit,
}: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftValue, setDraftValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const startEditing = useCallback(() => {
        if (readonly) {
            return;
        }
        setDraftValue(value);
        setIsEditing(true);
    }, [readonly, value]);

    const commit = useCallback(() => {
        setIsEditing(false);
        if (draftValue !== value) {
            onCommit(draftValue);
            return;
        }
        if (draftValue.trim().length === 0) {
            onBlankExit?.();
        }
    }, [draftValue, onBlankExit, onCommit, value]);

    const cancel = useCallback(() => {
        setDraftValue(value);
        setIsEditing(false);
        if (value.trim().length === 0) {
            onBlankExit?.();
        }
    }, [onBlankExit, value]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                commit();
            } else if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                cancel();
            }
        },
        [cancel, commit],
    );

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                className={classNames(
                    styles.inlineInput,
                    {
                        [styles.inlineInputError]: hasError,
                    },
                    valueClassName,
                )}
                value={draftValue}
                inputMode={inputMode}
                placeholder={placeholder}
                onChange={(event) => setDraftValue(event.target.value)}
                onBlur={commit}
                onKeyDown={handleKeyDown}
            />
        );
    }

    return (
        <EditableCellContent
            readonly={readonly}
            isEmpty={value.length === 0}
            // Surfaces the otherwise-hidden "Add" button (in the warning color)
            // when the empty value is invalid or explicitly flagged, so blank
            // required rows like a missing account name are visibly spottable.
            required={
                (hasError || flagWhenEmpty === true) && value.length === 0
            }
            addText={addText}
            hideIcon
            onClick={startEditing}
            className={styles.editableValueHug}
            classesClassName={classNames(
                {
                    [styles.inlineErrorText]: hasError,
                },
                valueClassName,
            )}
        >
            <span className={styles.inlineValueWrapper}>
                <span>{value}</span>
                {displayAddon}
            </span>
        </EditableCellContent>
    );
};
